import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ApiError, api } from '../api/client.js'
import type { CoverageResponse } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { PersonPicker } from '../components/people.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Notice, Spinner } from '../components/ui.js'
import { addDays, formatDate, formatWeekday, todayIso } from '../lib/format.js'
import { PageHead } from './Layout.js'

interface Group {
  siteId: string
  siteName: string
  address: string
  tasks: CoverageResponse['tasks']
}


/**
 * Vertretung, wörtlich gewünscht: "heute morgen kommt die Krankmeldung,
 * dann bräuchte ich alle Aufgaben des Mitarbeiters auf einen Blick und ordne
 * sie neu zu". One card per Liegenschaft, tick off what moves, choose the
 * person, one button, one undo. "Aufteilen" opens a person per task, so a
 * site is split between several people in the same step (Workshop 23.10.).
 *
 * The period may lie months ahead. Tasks exist only up to `horizon`; the
 * person chosen per Liegenschaft is saved as a rule, and tasks created later
 * in the period go to that person by themselves.
 */
export function CoveragePage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const toast = useToast()
  const today = todayIso()
  const [search] = useSearchParams()
  const [from, setFrom] = useState(search.get('von') ?? today)
  const [to, setTo] = useState(search.get('bis') ?? addDays(today, 4))
  // Person for the Liegenschaften without their own choice, saved as a rule too.
  const [fallback, setFallback] = useState('')
  const [assignee, setAssignee] = useState<Record<string, string>>({})
  const [selected, setSelected] = useState<Set<string>>(new Set())
  // Sites being split: there each task may go to someone other than the site's person.
  const [split, setSplit] = useState<Set<string>>(new Set())
  const [taskAssignee, setTaskAssignee] = useState<Record<string, string>>({})

  const coverage = useQuery({
    queryKey: ['coverage', id, from, to],
    queryFn: () => api<CoverageResponse>(`/api/staff/${id}/coverage?from=${from}&to=${to}`),
    enabled: Boolean(id) && to >= from,
  })

  const groups = useMemo<Group[]>(() => {
    const map = new Map<string, Group>()
    const address = (street: string | null, zip: string | null, city: string | null) =>
      [street, [zip, city].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    for (const site of coverage.data?.planSites ?? []) {
      map.set(site.siteId, { siteId: site.siteId, siteName: site.siteName, address: address(site.street, site.zip, site.city), tasks: [] })
    }
    for (const task of coverage.data?.tasks ?? []) {
      const key = task.siteId ?? task.objectId
      const group = map.get(key) ?? { siteId: key, siteName: task.siteName, address: address(task.street, task.zip, task.city), tasks: [] }
      group.tasks.push(task)
      map.set(key, group)
    }
    return [...map.values()].sort((a, b) => a.siteName.localeCompare(b.siteName, 'de'))
  }, [coverage.data])

  // Fresh data: everything ticked, people as saved before for this period.
  // Who takes over is decided on the phone, not by the tool (Workshop 23.10.:
  // no suggestions).
  useEffect(() => {
    if (!coverage.data) return
    setSelected(new Set(coverage.data.tasks.map((t) => t.id)))
    const saved = coverage.data.rules.filter((r) => r.from === coverage.data.from && r.until === coverage.data.to)
    setAssignee(Object.fromEntries(saved.filter((r) => r.siteId).map((r) => [r.siteId!, r.substituteId])))
    setFallback(saved.find((r) => !r.siteId)?.substituteId ?? '')
    setSplit(new Set())
    setTaskAssignee({})
  }, [coverage.data])

  const save = useMutation({
    mutationFn: (body: { assignments: { id: string; assigneeId: string | null }[]; rules: { siteId: string | null; substituteId: string }[] }) =>
      api<{ updated: number; previous: { id: string; assigneeId: string | null }[]; ruleIds: string[] }>(`/api/staff/${id}/cover`, {
        method: 'POST',
        body: { from: coverage.data!.from, to: coverage.data!.to, ...body },
      }),
    onSuccess: async (result, body) => {
      const later = coverage.data && coverage.data.to > coverage.data.horizon && body.rules.length > 0
      const text =
        result.updated > 0
          ? `${result.updated} Aufgaben neu verteilt.${later ? ' Spätere Aufgaben gehen automatisch an die Vertretung.' : ''}`
          : 'Vertretung gespeichert. Die Aufgaben gehen beim Anlegen an die Vertretung.'
      toast(text, {
        action: {
          label: 'Rückgängig',
          onAction: async () => {
            if (result.previous.length) await api('/api/occurrences/assign', { method: 'POST', body: { assignments: result.previous } })
            for (const ruleId of result.ruleIds) await api(`/api/cover-rules/${ruleId}`, { method: 'DELETE' })
            toast('Vertretung zurückgenommen.')
            void queryClient.invalidateQueries({ queryKey: ['coverage'] })
            void queryClient.invalidateQueries({ queryKey: ['cover-rules'] })
          },
        },
      })
      await queryClient.invalidateQueries({ queryKey: ['coverage'] })
      await queryClient.invalidateQueries({ queryKey: ['cover-rules'] })
      await queryClient.invalidateQueries({ queryKey: ['occurrences'] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Verteilen fehlgeschlagen.', 'error'),
  })

  const siteChoice = (group: Group) => assignee[group.siteId] || fallback || null
  const personFor = (group: Group, taskId: string) => (split.has(group.siteId) && taskAssignee[taskId]) || siteChoice(group)
  const planned = groups.flatMap((group) =>
    group.tasks.filter((t) => selected.has(t.id)).map((t) => ({ id: t.id, assigneeId: personFor(group, t.id) })),
  )
  const rules = [
    ...groups.filter((g) => assignee[g.siteId]).map((g) => ({ siteId: g.siteId, substituteId: assignee[g.siteId]! })),
    ...(fallback ? [{ siteId: null, substituteId: fallback }] : []),
  ]
  const missingPerson = planned.some((p) => !p.assigneeId)
  const people = new Set(planned.map((p) => p.assigneeId).filter(Boolean)).size
  const beyond = coverage.data && coverage.data.to > coverage.data.horizon ? coverage.data.horizon : null

  function toggleSplit(siteId: string) {
    setSplit((current) => {
      const next = new Set(current)
      if (next.has(siteId)) next.delete(siteId)
      else next.add(siteId)
      return next
    })
  }

  function toggle(taskId: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(taskId)) next.delete(taskId)
      else next.add(taskId)
      return next
    })
  }

  const person = coverage.data?.person

  return (
    <>
      <PageHead
        title={person ? `Vertretung für ${person.name}` : 'Vertretung'}
        subtitle="Krankmeldung oder Urlaub: offene Aufgaben im Zeitraum auf andere verteilen."
        action={
          person && (
            <Link className="btn secondary" to={`/mitarbeiter/${person.id}`}>
              Zur Person
            </Link>
          )
        }
      />

      <div className="toolbar">
        <div className="toolbar-row">
          <label className="field">
            <span>Von</span>
            <input type="date" value={from} min={today} onChange={(e) => e.target.value && setFrom(e.target.value)} />
          </label>
          <label className="field">
            <span>Bis</span>
            <input type="date" value={to} min={from} max={coverage.data?.maxTo} onChange={(e) => e.target.value && setTo(e.target.value)} />
          </label>
          <div className="quick-range">
            {[
              ['Heute', 0],
              ['3 Tage', 2],
              ['1 Woche', 6],
              ['2 Wochen', 13],
            ].map(([label, days]) => (
              <button
                key={label as string}
                type="button"
                className="btn secondary small"
                onClick={() => {
                  setFrom(today)
                  setTo(addDays(today, days as number))
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="grow" />
          <label className="field filter-object">
            <span>Alles an eine Person</span>
            <PersonPicker
              value={fallback}
              dense
              emptyLabel="Keine Auswahl"
              placeholder="Namen tippen, gilt für alle übrigen Liegenschaften"
              onChange={(pid) => setFallback(pid)}
            />
          </label>
        </div>
      </div>

      {beyond && (
        <Notice kind="info">
          Aufgaben gibt es bisher bis zum {formatDate(beyond)}. Die Vertretung wird für den ganzen Zeitraum gespeichert, spätere Aufgaben gehen beim Anlegen
          von selbst an die gewählte Person.
        </Notice>
      )}

      {coverage.isLoading ? (
        <Spinner />
      ) : !coverage.data || groups.length === 0 ? (
        <section className="card">
          <EmptyState title="Keine offenen Aufgaben im Zeitraum" hint="Für diese Tage ist nichts zu verteilen." />
        </section>
      ) : (
        <div className="stack">
          {groups.map((group) => {
            if (group.tasks.length === 0 && !beyond) return null
            const chosen = assignee[group.siteId] ?? ''
            return (
              <section className="card" key={group.siteId}>
                <div className="card-head">
                  <div>
                    <h2>
                      <Link to={`/objekte/${group.siteId}`}>{group.siteName}</Link>
                    </h2>
                    <div className="muted small">
                      {group.address ? `${group.address}, ` : ''}
                      {group.tasks.length > 0 ? `${group.tasks.length} Aufgaben` : 'Aufgaben entstehen erst später'}
                    </div>
                  </div>
                  <div className="cover-head-actions">
                    <PersonPicker
                      value={chosen}
                      dense
                      emptyLabel={fallback ? 'wie oben für alle' : 'Nicht zugewiesen lassen'}
                      placeholder={split.has(group.siteId) ? 'Für alle übrigen' : fallback ? 'wie oben für alle' : 'Wer übernimmt?'}
                      onChange={(pid) => setAssignee({ ...assignee, [group.siteId]: pid })}
                    />
                    {group.tasks.length > 0 && <button type="button" className={`btn small ${split.has(group.siteId) ? '' : 'secondary'}`} aria-pressed={split.has(group.siteId)} onClick={() => toggleSplit(group.siteId)}>
                      {split.has(group.siteId) ? 'Nicht aufteilen' : 'Aufteilen'}
                    </button>}
                  </div>
                </div>
                {split.has(group.siteId) && (
                  <div className="card-body small muted" style={{ paddingBottom: 0 }}>
                    Für einzelne Aufgaben eine andere Person wählen. Alle übrigen gehen an die Person oben.
                  </div>
                )}
                <ul className="cover-list">
                  {group.tasks.map((task) => (
                    <li key={task.id}>
                      <label>
                        <input type="checkbox" checked={selected.has(task.id)} onChange={() => toggle(task.id)} />
                        <span className="when">
                          {formatWeekday(task.dueDate)}, {formatDate(task.dueDate)}
                          {task.dueTime ? `, bis ${task.dueTime}` : ''}
                        </span>
                        <span className="strong">{task.title}</span>
                        <span className="muted small">{task.path.join(' › ') || 'gesamte Liegenschaft'}</span>
                      </label>
                      {split.has(group.siteId) && selected.has(task.id) && (
                        <div className="cover-task-person">
                          <PersonPicker
                            value={taskAssignee[task.id] ?? ''}
                            dense
                            emptyLabel="wie oben"
                            placeholder="wie oben"
                            onChange={(pid) => setTaskAssignee({ ...taskAssignee, [task.id]: pid })}
                          />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )
          })}
        </div>
      )}

      {groups.length > 0 && (
        <div className="sticky-actions">
          {missingPerson && <Notice kind="warn">Für mindestens eine Aufgabe ist niemand gewählt. Diese Aufgaben werden dann ohne Zuständigen geführt.</Notice>}
          <div className="spread">
            <span className="strong">
              {planned.length} von {coverage.data?.tasks.length ?? 0} Aufgaben werden verteilt
              {people > 1 ? ` auf ${people} Personen` : ''}
              {beyond && rules.length > 0 ? ', spätere folgen automatisch' : ''}
            </span>
            <button
              type="button"
              className="btn"
              disabled={(planned.length === 0 && rules.length === 0) || save.isPending}
              onClick={() => save.mutate({ assignments: planned, rules })}
            >
              <Icon name="swap" /> Verteilen
            </button>
          </div>
        </div>
      )}
    </>
  )
}
